// Adapted from apps/admin/src/pages/dashboard/gps-tracking/lib/utils.ts (PRD #875): only the
// helpers this screen uses; the driver groups and the status colour stay in admin.
import { ConnectProto } from "@racoon/api";
import type { GpsTrackingPoint } from "../model/types";

/** Полное имя водителя смены; пустая строка, если водителя нет. */
export function getDriverName(shift: ConnectProto.DriverOnShift): string {
  return `${shift.driver?.firstName || ""} ${shift.driver?.lastName || ""}`.trim();
}

/**
 * Получает последнюю позицию из массива GPS точек по vehicleId
 */
export function getLastPositionByVehicleId<T extends { vehicleId: string; timestamp: Date }>(
  points: T[],
  vehicleId: string,
): T | undefined {
  const vehiclePoints = points.filter((p) => p.vehicleId === vehicleId);
  if (vehiclePoints.length === 0) return undefined;

  return vehiclePoints.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
}

/** GetTruckTravelPath's `geojsonFeature`: a LineString + parallel per-point arrays. */
type TravelPathFeature = {
  geometry: { coordinates: number[][] };
  properties: { timestamps?: number[]; speeds?: number[]; angles?: number[] };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

// The feature arrives as an untyped `google.protobuf.Struct`, so its shape is checked here.
const isTravelPathFeature = (feature: unknown): feature is TravelPathFeature =>
  isRecord(feature) &&
  isRecord(feature.geometry) &&
  Array.isArray(feature.geometry.coordinates) &&
  feature.geometry.coordinates.every(Array.isArray) &&
  isRecord(feature.properties);

/**
 * Turns a truck's travel path (GeoJSON LineString + parallel timestamp / speed /
 * angle arrays, as returned by GetTruckTravelPath) into GPS points. Returns []
 * for a missing or malformed feature.
 */
export function travelPathToGpsPoints(
  feature: unknown,
  vehicle: { id: bigint; plateNumber: string },
  driverNames: string[],
): GpsTrackingPoint[] {
  if (!isTravelPathFeature(feature)) return [];
  const { timestamps, speeds, angles } = feature.properties;
  return feature.geometry.coordinates.map((coord, i) => ({
    id: `point-${vehicle.id}-${i}`,
    vehicleId: String(vehicle.id),
    plateNumber: vehicle.plateNumber,
    driverNames,
    timestamp: new Date((timestamps?.[i] || 0) * 1000),
    lat: coord[1],
    lon: coord[0],
    speed: speeds?.[i] || 0,
    angle: angles?.[i] || 0,
  }));
}
